/**
 * SourceClassifyControl — Phase 1 PR 10 (additive; not mounted in this PR).
 *
 * Four actions: Approve / Forwarder / Not mine / Dismiss. Two modes:
 *
 *   - Single-source: pass `sourceId` + `currentClassification`. Renders
 *     a button group where the active classification is visually selected;
 *     clicking the active one is a no-op (a re-PATCH would still succeed
 *     server-side but is wasted load).
 *
 *   - Bulk: pass `sourceIds` (length ≥ 1). Renders the same buttons but
 *     no "selected" state — bulk targets may have mixed classifications.
 *     Calls `bulkClassifySources`. The Dismiss button is hidden in bulk
 *     mode for now; bulk dismiss is a follow-up if the UI needs it.
 *
 * Wired into the page in PR 11. Until then the component lives behind no
 * mount point — it ships compiled but unrendered, which is the whole
 * point of extracting it: the page rewrite in PR 11 has one fewer thing
 * to review at the time.
 *
 * No upstream confirmation modals — the operations are reversible
 * (re-PATCH back to any other value, or to null) and a confirmation dance
 * adds friction to what should be one-click triage. If destructive intent
 * grows, a confirmation step lives at the caller, not here.
 */

import { useState } from "react";
import {
  ShieldCheck,
  Repeat,
  ShieldOff,
  EyeOff,
  Loader2,
  Check,
} from "lucide-react";
import { api, type SpineClassification } from "@/lib/api";
import { toast } from "@/lib/toast";

interface SingleProps {
  mode?: "single";
  sourceId: string;
  /** The currently-applied classification; drives the "selected" visual state. */
  currentClassification: SpineClassification;
  /**
   * Known-provider category (esp / transactional / marketing / crm /
   * infrastructure / forwarder / unknown). Drives PR 34's state-aware
   * suppression — e.g. the "It's a forwarder" button is hidden on
   * ESPs because their category proves they're not forwarders.
   * Optional; legacy callers that don't pass it get the full button
   * row (matches pre-PR-34 behaviour).
   */
  providerCategory?: string | null;
  /** Whether this Source is currently dismissed; drives the Dismiss button label. */
  dismissed?: boolean;
  /**
   * Optional callback invoked after a successful action so the page can
   * refresh the source row (or the list). Receives the new value so the
   * parent can optimistically update without a refetch.
   */
  onClassified?: (next: SpineClassification) => void;
  onDismissed?: (next: { dismissed: boolean }) => void;
  /**
   * Phase 3 PR 28 — optional pre-classify hook so a wrapper component
   * can inject a confirmation step (e.g. the SPF budget guard that
   * intercepts Approve clicks when the source's provider include
   * would push the SPF chain over the RFC 7208 §4.6.4 limit).
   *
   * Return `true` (or undefined) to proceed; return `false` to abort
   * the classify without firing the PATCH or the toast. Keeping this
   * an opt-in callback preserves the component's "no upstream
   * confirmation modals" default — wrappers compose, the base
   * component stays terse.
   */
  onBeforeClassify?: (target: SpineClassification) => Promise<boolean> | boolean;
}

interface BulkProps {
  mode: "bulk";
  sourceIds: string[];
  onClassified?: (next: { classification: SpineClassification; appliedCount: number }) => void;
}

export type SourceClassifyControlProps = SingleProps | BulkProps;

/** Action descriptor used to render the button row uniformly across modes. */
interface ClassifyAction {
  key: "Approved" | "Forwarder" | "Not-Mine";
  label: string;
  Icon: typeof ShieldCheck;
  /** Tailwind class fragments for the active "selected" pill in single mode. */
  activeColor: string;
}

const ACTIONS: readonly ClassifyAction[] = [
  {
    key: "Approved",
    label: "Approve",
    Icon: ShieldCheck,
    activeColor: "bg-emerald-100 text-emerald-900 border-emerald-300",
  },
  {
    key: "Forwarder",
    label: "It's a forwarder",
    Icon: Repeat,
    activeColor: "bg-sky-100 text-sky-900 border-sky-300",
  },
  {
    key: "Not-Mine",
    label: "Not mine",
    Icon: ShieldOff,
    activeColor: "bg-rose-100 text-rose-900 border-rose-300",
  },
];

/**
 * Provider categories that are emphatically not forwarders — ESPs,
 * transactional services, marketing platforms, CRMs. Offering "It's
 * a forwarder" on these sources is the L2 finding from the
 * post-redesign review: a static action template that ignores what
 * the matcher already knows about the sender.
 *
 * `infrastructure` (Google Workspace, Microsoft 365, …) is OUT of
 * this set. Infrastructure providers can legitimately function as
 * forwarders for the customer's own mail in some configurations
 * (e.g. Google Group → external alias), so the user gets to keep
 * the option there.
 *
 * `unknown` and `null` (synthetic-fallback sources) also stay out
 * — the matcher couldn't identify the provider, so we don't have
 * grounds to remove an option the user might need.
 */
const NON_FORWARDER_CATEGORIES = new Set([
  "esp",
  "transactional",
  "marketing",
  "crm",
]);

/**
 * Pick the subset of ACTIONS to render given the Source's current
 * state. PR 34 (M2 + L2) — closes the "Approve on already-Approved
 * source" + "It's a forwarder on a SendGrid row" dead-UI cases.
 *
 *   - Hide the action whose `key` already equals
 *     `currentClassification`. Re-clicking it is a no-op upstream
 *     (the active-pill render handled the no-op case before this
 *     PR; we now hide the button outright so the row reads as a
 *     decision-needed control, not a recap of an already-made one).
 *
 *   - Hide "Forwarder" when the provider category is in
 *     NON_FORWARDER_CATEGORIES AND the user hasn't manually set the
 *     source to Forwarder yet. (If they HAD set it, we hide via the
 *     first rule and they keep the active-state visual elsewhere.)
 *
 * Exported so the unit test can pin every cell of the matrix without
 * mounting the component.
 */
export function getVisibleClassifyActions(
  currentClassification: SpineClassification,
  providerCategory: string | null | undefined
): readonly ClassifyAction[] {
  return ACTIONS.filter((action) => {
    if (action.key === currentClassification) return false;
    if (
      action.key === "Forwarder" &&
      providerCategory !== undefined &&
      providerCategory !== null &&
      NON_FORWARDER_CATEGORIES.has(providerCategory)
    ) {
      return false;
    }
    return true;
  });
}

export function SourceClassifyControl(props: SourceClassifyControlProps) {
  // One pending action at a time across the button row, so a slow PATCH
  // doesn't let the user fire a second one against half-applied state.
  const [pending, setPending] = useState<string | null>(null);

  // Single-source path. Mode is implicit when `sourceIds` is absent.
  if (props.mode !== "bulk") {
    return <SingleClassifyRow {...props} pending={pending} setPending={setPending} />;
  }
  return <BulkClassifyRow {...props} pending={pending} setPending={setPending} />;
}

interface RowInternalProps {
  pending: string | null;
  setPending: (s: string | null) => void;
}

function SingleClassifyRow(
  props: SingleProps & RowInternalProps
) {
  const {
    sourceId,
    currentClassification,
    providerCategory,
    dismissed,
    onClassified,
    onDismissed,
    onBeforeClassify,
    pending,
    setPending,
  } = props;

  // PR 34 (M2 + L2). State-aware action list. See
  // getVisibleClassifyActions for the suppression rules. The active
  // classification's button drops out (no more dead "Approve" on
  // already-Approved rows); ESP/CRM/marketing/transactional sources
  // also drop "It's a forwarder" because their category disproves it.
  const visibleActions = getVisibleClassifyActions(
    currentClassification,
    providerCategory
  );

  async function handleClassify(target: SpineClassification) {
    if (pending) return;
    // Re-clicking the active classification is a no-op (server-side
    // success without effect; just save the round-trip).
    if (target === currentClassification) return;
    // Phase 3 PR 28 — give wrappers a chance to inject a confirmation
    // step before the PATCH fires. The SPF budget guard uses this
    // to open a modal when Approving would push the chain over.
    if (onBeforeClassify) {
      try {
        const proceed = await onBeforeClassify(target);
        if (!proceed) return;
      } catch (err) {
        // Defensive: a hook that throws shouldn't fire the PATCH OR
        // show a misleading "couldn't update" toast.
        console.error("onBeforeClassify hook failed:", err);
        return;
      }
    }
    setPending(target);
    try {
      const result = await api.classifySource(sourceId, target);
      toast.success(`Marked as ${labelFor(target)}`);
      onClassified?.(result.classification);
    } catch (err) {
      toast.error(
        "Couldn't update classification",
        err instanceof Error ? err.message : undefined
      );
    } finally {
      setPending(null);
    }
  }

  async function handleDismiss() {
    if (pending) return;
    const nextDismissed = !dismissed;
    setPending("dismiss");
    try {
      const result = await api.dismissSource(sourceId, nextDismissed);
      toast.success(result.dismissed ? "Dismissed" : "Un-dismissed");
      onDismissed?.({ dismissed: result.dismissed });
    } catch (err) {
      toast.error(
        "Couldn't update dismissal",
        err instanceof Error ? err.message : undefined
      );
    } finally {
      setPending(null);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* PR 34 (M2 + L2). Render only the actions the source's current
          state permits. The active classification's button drops out
          (no more "Approve" on already-Approved rows); ESP/CRM/
          marketing/transactional sources also drop "It's a forwarder"
          because the provider category proves they're not. */}
      {visibleActions.map((action) => {
        const isPending = pending === action.key;
        return (
          <button
            key={action.key}
            type="button"
            onClick={() => handleClassify(action.key)}
            disabled={pending !== null}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            {isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <action.Icon className="w-4 h-4" />
            )}
            {action.label}
          </button>
        );
      })}

      {/* Active-state recap pill (PR 34). The classification button
          used to double as the "selected" indicator (emerald Approve
          pill with a checkmark on an Approved row). Now that the
          button is hidden when active, surface the same signal as a
          non-interactive pill so the user still sees what state the
          source is in — and so the row doesn't read as "no decisions
          yet" when the user already approved it. */}
      {currentClassification !== "Unclassified" && (
        <ActiveStatePill classification={currentClassification} />
      )}

      <button
        type="button"
        onClick={handleDismiss}
        disabled={pending !== null}
        aria-pressed={!!dismissed}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          dismissed
            ? "bg-slate-200 border-slate-400 text-slate-800"
            : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
        }`}
      >
        {pending === "dismiss" ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <EyeOff className="w-4 h-4" />
        )}
        {dismissed ? "Un-dismiss" : "Dismiss for now"}
      </button>
    </div>
  );
}

/**
 * Non-interactive recap of the source's currently-applied
 * classification. Rendered when the user has already classified the
 * source so the row carries a visible signal of the active state
 * even though the matching action button is hidden (PR 34).
 *
 * Visually mirrors the active-pill colours the buttons used pre-PR-34
 * — emerald for Approved, sky for Forwarder, rose for Not-Mine — so
 * the change is muscle-memory-compatible. Includes the checkmark
 * icon so it reads as "this is the chosen state", not as another
 * tappable control.
 */
function ActiveStatePill({
  classification,
}: {
  classification: Exclude<SpineClassification, "Unclassified">;
}) {
  const action = ACTIONS.find((a) => a.key === classification);
  if (!action) return null;
  return (
    <span
      aria-label={`Currently classified as ${action.label}`}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-medium ${action.activeColor}`}
    >
      <Check className="w-4 h-4" />
      {action.label}
    </span>
  );
}

function BulkClassifyRow(props: BulkProps & RowInternalProps) {
  const { sourceIds, onClassified, pending, setPending } = props;

  async function handleClassify(target: SpineClassification) {
    if (pending) return;
    if (sourceIds.length === 0) return;
    setPending(target);
    try {
      const result = await api.bulkClassifySources(sourceIds, target);
      toast.success(
        `Updated ${result.appliedCount} source${result.appliedCount === 1 ? "" : "s"}`,
        result.appliedCount < result.requestedCount
          ? `${result.requestedCount - result.appliedCount} not updated`
          : undefined
      );
      onClassified?.({
        classification: result.classification,
        appliedCount: result.appliedCount,
      });
    } catch (err) {
      toast.error(
        "Couldn't update classifications",
        err instanceof Error ? err.message : undefined
      );
    } finally {
      setPending(null);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-slate-600 mr-1">
        Mark {sourceIds.length} source{sourceIds.length === 1 ? "" : "s"} as:
      </span>
      {ACTIONS.map((action) => {
        const isPending = pending === action.key;
        return (
          <button
            key={action.key}
            type="button"
            onClick={() => handleClassify(action.key)}
            disabled={pending !== null || sourceIds.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border bg-white border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <action.Icon className="w-4 h-4" />
            )}
            {action.label}
          </button>
        );
      })}
    </div>
  );
}

function labelFor(c: SpineClassification): string {
  switch (c) {
    case "Approved":
      return "Approved";
    case "Forwarder":
      return "Forwarder";
    case "Not-Mine":
      return "Not mine";
    default:
      return "Unclassified";
  }
}
